import * as repo from '../repository/depositRepository';
import { DepositListItem, DepositUIStatus, OrderCandidate, ParsedDeposit, DepositMatch, DepositIncomingCard, ShippingRescue, RescueRevalidation } from '../types/deposit';
import { ShippingConfig } from '../shippingAmounts';
import { normalizeName } from '../normalizeName';

// UTC 인스턴트를 KST(+9) 기준 yyyy-MM-dd 로. 입금일 기준 영업일 비교용.
export function toKstDateString(iso: string, offsetDays = 0): string {
  const d = new Date(iso);
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000 + offsetDays * 24 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
}

// 입금액과의 |델타| 최소 우선. JS sort는 stable → 동률은 입력(윈도우 order_date desc/id desc) 순 유지.
function amountDelta(c: OrderCandidate, amount: number): number {
  let best = Infinity;
  for (const a of c.expectedAmounts) best = Math.min(best, Math.abs(a - amount));
  return best === Infinity ? Number.MAX_SAFE_INTEGER : best;
}
function sortByAmountDelta(cands: OrderCandidate[], amount: number): OrderCandidate[] {
  return [...cands].sort((a, b) => amountDelta(a, amount) - amountDelta(b, amount));
}

// 시간 게이트 (스펙 2026-07-24 §8): 자동 제안은 마지막 완납(paid_at) 이후 입금만.
// 과거 강등 이력 주문(paid_at 有)에서 이전 결제 사이클의 입금이 확신 후보로 부활하는 것을 차단.
// 수동 피커에는 적용하지 않는다 (수동 선택 = 셀러의 능동 결정).
function depositEffectiveTimeMs(deposit: ParsedDeposit): number {
  return new Date(deposit.deposited_at ?? deposit.created_at).getTime();
}
// 입금 시각은 클로저에서 1회만 계산 — 같은 입금 × 윈도우 주문 M건에 반복 Date 파싱 방지 (codexloop B-R1 F3).
function paidAtGateFor(deposit: ParsedDeposit): (order: OrderCandidate) => boolean {
  const depositTimeMs = depositEffectiveTimeMs(deposit);
  return (order) => order.paidAt == null || depositTimeMs > new Date(order.paidAt).getTime();
}

// 확정·제외 행 일괄 빌드. 확정 행마다 주문을 1건씩 조회하면 확정 N건 = 동시 요청 N개가 되어
// 대량 확정 계정의 큐 로드가 통째로 실패한다(2026-07-31 사고: 확정 480건). 주문 id를 dedupe해
// 1회 조회 후 Map으로 배분 — 분할입금으로 1주문:N입금이라 실측 480행 → 333 distinct.
// matched_order_id 판정은 기존 per-row 경로와 동일한 truthiness 유지(값 0 취급 동등).
async function buildSettledItems(
  settled: { deposit: ParsedDeposit; match: DepositMatch }[], config: ShippingConfig, userId: string,
): Promise<DepositListItem[]> {
  const orderIds = [...new Set(
    settled.flatMap((r) => (r.match.confirmed_at && r.match.matched_order_id ? [r.match.matched_order_id] : [])),
  )];
  const orders = await repo.findOrdersByIds(orderIds, config, userId);
  const byId = new Map(orders.map((o) => [o.id, o]));
  // 확정 우선 판정 = 기존 early-return 순서 보존(confirmed_at이 seller_rejected보다 우선).
  return settled.map((r) => r.match.confirmed_at
    ? {
        deposit: r.deposit, match: r.match, uiStatus: 'confirmed' as DepositUIStatus, candidates: [],
        confirmedOrder: r.match.matched_order_id ? byId.get(r.match.matched_order_id) ?? null : null,
      }
    : { deposit: r.deposit, match: r.match, uiStatus: 'excluded' as DepositUIStatus, candidates: [], confirmedOrder: null });
}

// 미확정·비제외 행의 도출 재료. fetch(병렬)와 derive(순차)를 분리해 네트워크 병렬성은
// 기존(Promise.all)과 동일하게 유지하면서 claimed 배정만 결정적 순차로 만든다.
interface PendingFetch {
  deposit: ParsedDeposit;
  match: DepositMatch;
  storedLive: OrderCandidate[]; // candidate_order_ids의 live 미지정 주문(엣지 매칭 잔존 or 찜)
  inWindow: OrderCandidate[];   // 입금일 -14~+1 윈도우 미지정 주문(원본 — 자동 경로 소비 금지, 아래 eligible만)
  // 시간 게이트 통과분(스펙 §8) — 모든 자동 도출(이름 권위·동적 후보·claim·pin)은 이 집합만 소비.
  // 표시 직전 필터가 아니라 선계산: 시간상 부적격 주문이 이름 권위·claim·pin 슬롯을 선점하지 못하게.
  eligibleInWindow: OrderCandidate[];
}

async function fetchPendingData(
  deposit: ParsedDeposit, match: DepositMatch, config: ShippingConfig, userId: string,
  fetchWindow: (from: string, to: string) => Promise<OrderCandidate[]>,
): Promise<PendingFetch> {
  const base = deposit.deposited_at ?? deposit.created_at;
  const from = toKstDateString(base, -14);
  const to = toKstDateString(base, 1);
  // 저장 후보가 있어도 inWindow를 항상 함께 로드(기존엔 폴백 시에만) — 저장 후보가 전부
  // 배정돼 폴백할 때 추가 왕복 없이 derive가 순수함수로 남기 위함. 윈도우 쿼리는 호출측이
  // 날짜범위별로 캐시해 같은 날 입금 N건이 쿼리 1회를 공유(codexloop R1 F6 — 요청 폭증 방지).
  const [storedLiveRaw, inWindow] = await Promise.all([
    match.candidate_order_ids.length > 0
      ? repo.findOrdersByIds(match.candidate_order_ids, config, userId)
      : Promise.resolve([] as OrderCandidate[]),
    fetchWindow(from, to),
  ]);
  const gate = paidAtGateFor(deposit);
  return {
    deposit, match,
    // 부분 입금으로 전이된 찜 후보는 금액 재검증 통과 시에만 신뢰(잔액≠입금액이면 dead-pin과
    // 동일하게 동적 재도출로 강등 — stale 확신 표시 방지, codexloop 분할 R1 #4)
    // + 시간 게이트(스펙 §8): paid_at 이전 입금의 찜은 dead-pin과 동일하게 강등.
    storedLive: storedLiveRaw.filter((o) =>
      (o.status === '미지정' ||
        (o.status === '부분 입금' && o.expectedAmounts.includes(deposit.amount))) &&
      gate(o)
    ),
    inWindow,
    eligibleInWindow: inWindow.filter(gate),
  };
}

interface PinRequest { depositMatchId: number; orderId: number; expectedModifiedAt: string; }
// stale 찜(이름모순/죽은 찜) → 새 확신 추천으로 단일 트랜잭션 X→Y 교체(codexloop R4 —
// unpin→pin 2-요청 체인 폐기: 빈 배열 중간상태가 없어야 지연 pin의 옛 후보 부활 ABA가 안 생긴다).
// 교체할 새 후보가 없는 stale 찜은 DB에 남기고 표시에서만 무시(확신 후보가 생기면 그때 교체).
interface ReplaceRequest { depositMatchId: number; expectedOrderId: number; newOrderId: number; expectedModifiedAt: string; }

// 증분2(스펙 D1·D2): 입금자명 키의 유효 닉네임 집합 = 학습맵 ∪ {윈도우 내 열린 주문 중
// normalizeName(닉네임)==normalizeName(입금자명)인 닉네임(raw)}. 서버 matchDeposit과 동일
// 의미론. 모든 클라 이름 판정(핀 모순·저장 후보 재판정·동적 도출·pass1 우선권)이 이 헬퍼를
// 써야 판정이 어긋나지 않는다. undefined = 이름 완전 미상(기존 map.get miss와 동일 시맨틱).
// 빈 이름 가드: key가 빈 문자열이면 직접 일치 스킵.
function effectiveNicks(
  depositorName: string,
  nameMap: Map<string, Set<string>>,
  windowOrders: OrderCandidate[],
): Set<string> | undefined {
  const key = normalizeName(depositorName);
  const nicks = new Set(nameMap.get(key) ?? []);
  if (key) {
    for (const o of windowOrders) {
      if (normalizeName(o.nickname) === key) nicks.add(o.nickname);
    }
  }
  return nicks.size > 0 ? nicks : undefined;
}

// 모순 찜 판정(codexloop R2 F3): 찜 행(match_status='unmatched', 후보 1건)인데 학습된 이름
// 집합이 찜 후보 닉네임을 배제 — 금액-only 찜이 권위 있는 이름 증거와 충돌한 상태.
// 파티션(pass 분류)·저장경로·byAmount 게이트 세 곳이 반드시 같은 판정을 쓰도록 헬퍼로 고정.
function isPinContradicted(f: PendingFetch, nameMap: Map<string, Set<string>>): boolean {
  if (f.storedLive.length !== 1 || f.match.match_status !== 'unmatched') return false;
  const nicks = effectiveNicks(f.deposit.depositor_name, nameMap, f.eligibleInWindow);
  return !!nicks && !nicks.has(f.storedLive[0].nickname);
}

// 순수 도출(await 없음). claimed = 앞선 입금(저장 후보/확신 추천)이 선점한 주문 —
// 뒤 입금의 동적 후보에서 제외해, 나중 입금이 먼저 온 입금의 추천을 흔들지 못하게 한다(스펙 G2).
function derivePendingItem(
  f: PendingFetch,
  nameMap: Map<string, Set<string>>,
  claimed: Set<number>,
  pins: PinRequest[],
  replaces: ReplaceRequest[],
): DepositListItem {
  const { deposit, match } = f;
  const pinContradicted = isPinContradicted(f, nameMap);

  // 저장 후보 경로(기존 sticky 동작 그대로): 후보가 살아있는 한 재계산으로 풀리지 않는다.
  if (f.storedLive.length >= 2) {
    return { deposit, match, uiStatus: 'ambiguous', candidates: f.storedLive, confirmedOrder: null };
  }
  if (f.storedLive.length === 1 && !pinContradicted) {
    const c = f.storedLive[0];
    claimed.add(c.id);
    // 표시 등급: 엣지가 이름 확신으로 저장한 행(match_status!=='unmatched')은 기존대로 matched.
    // 찜 행(match_status==='unmatched' — 엣지는 unmatched에 후보를 저장하지 않으므로 찜이 유일한
    // 출처)은 이름 학습 여부로 재판정(동적 도출과 동일 기준 — 파랑 확신 단계 보존, 스펙 §3.4).
    let uiStatus: DepositUIStatus = 'matched';
    if (match.match_status === 'unmatched') {
      const nicks = effectiveNicks(deposit.depositor_name, nameMap, f.eligibleInWindow);
      uiStatus = nicks?.has(c.nickname) ? 'matched' : 'amount_suggested';
    }
    return { deposit, match, uiStatus, candidates: [c], confirmedOrder: null };
  }
  // stale 찜(codexloop R2 F3 + R3-4): ①이름모순 찜(위에서 pinContradicted) ②찜 주문이 배정/삭제돼
  // 죽은 찜(storedLive 0인데 저장 배열 잔존). 둘 다 동적 재도출로 진행하고, 확신 대체 후보가
  // 도출되면 replace(X→Y 단일 트랜잭션)를 큐잉한다. 찜은 항상 singleton이라 length===1 검사
  // (엣지 저장 행은 match_status 가드로 여기 못 온다).
  const stalePinOrderId =
    match.match_status === 'unmatched' && match.candidate_order_ids.length === 1
      && (pinContradicted || f.storedLive.length === 0)
      ? match.candidate_order_ids[0]
      : null;

  // 동적 도출 — 기존 buildListItem dynamic 분기와 동일 로직(원 주석 유지). 델타 = claimed 제외 +
  // 이름 권위는 '무필터' 윈도우로 판정(codexloop R1 F4: 그 이름의 주문이 전부 선점됐다고 무관
  // 닉네임의 금액일치 분기로 추락하면 이름 우선 원칙 위반 → fail-closed unmatched).
  // ('무필터' = claimed 미적용 의미 — paid_at 시간 게이트는 적용된 eligible 집합 기준, 스펙 §8.)
  let candidates: OrderCandidate[] = [];
  let uiStatus: DepositUIStatus;

  // ★이름 우선(오너 결정 2026-06-24): 입금자명이 학습돼 그 닉네임의 열린주문이 윈도우에 있으면
  //   '이름 일치'가 우연한 무관 닉네임의 금액일치보다 우선한다. 그래야 무관 닉네임 B의 우연한
  //   금액일치를 amount_suggested(파란 확실카드)로 띄워 잘못 확정→다리 오염하는 것을 막는다.
  const nicks = effectiveNicks(deposit.depositor_name, nameMap, f.eligibleInWindow);
  const byNameAll = nicks ? f.eligibleInWindow.filter((o) => nicks.has(o.nickname)) : [];

  if (byNameAll.length >= 1) {
    // 이름 학습됨 + 열린주문 존재 → 그 이름의 주문 안에서만 판정(무관 닉네임 무시).
    const byName = byNameAll.filter((o) => !claimed.has(o.id));
    if (byName.length === 0) {
      // 그 이름의 열린주문이 전부 앞선 입금에 선점됨 → byAmount 추락 금지, fail-closed(R1 F4).
      uiStatus = 'unmatched';
    } else {
    const byNameExact = byName.filter((o) => o.expectedAmounts.includes(deposit.amount));
    if (byNameExact.length === 1) { candidates = byNameExact; uiStatus = 'matched'; }
    else if (byNameExact.length >= 2) { candidates = byNameExact; uiStatus = 'ambiguous'; }
    else { candidates = sortByAmountDelta(byName, deposit.amount); uiStatus = 'amount_mismatch'; }  // 단계4 amber
    }
  } else if (match.match_reason === 'name-unknown' && !pinContradicted) {
    // ★모순 찜 행은 byAmount 금지(codexloop R2 F3): 그 이름의 열린주문이 지금 없다고 금액 분기로
    //   떨어지면 방금 모순 판정한 바로 그 주문을 다시 amount_suggested로 재선택하는 자기모순.
    // 엣지가 SMS 시점에 입금자명을 '몰랐던' 경우만 금액기반 제안(기존 amount_suggested 보존).
    // non-goal(codexloop R5 합의): SMS시 name-unknown이었다가 이후 띄어쓰기-변형 이름으로만
    // 학습되면 scoped raw-IN이 놓쳐 여전히 byAmount를 탈 수 있음. 구식 미지정 입금의 소급
    // name-dominance는 미목표 — 정규화/distinct RPC 도입 시 해소(스펙 미룬 후속).
    const byAmount = f.eligibleInWindow.filter((o) => !claimed.has(o.id) && o.expectedAmounts.includes(deposit.amount));
    if (byAmount.length === 1) { candidates = byAmount; uiStatus = 'amount_suggested'; }
    else if (byAmount.length >= 2) { candidates = byAmount; uiStatus = 'ambiguous'; }
    else uiStatus = 'unmatched';
  } else {
    // 엣지는 이름을 알았으나(amount-no-match/no-open-order 등) 프론트 스코프 맵이 그 이름을 놓침
    // (raw-IN 정규화 갭: parsed_order.name "김 민지" ≠ 입금 "김민지") 또는 그 이름의 열린주문이
    // 윈도우 밖. 이때 무관 닉네임의 우연한 금액일치를 amount_suggested(파란 확실카드)로 띄우면
    // 잘못 확정→다리 오염. 그래서 fail-closed = unmatched(오너 '이름 우선' 결정과 일관, codexloop R4).
    uiStatus = 'unmatched';
  }

  // 확신 추천(후보 1건: matched/amount_suggested)만 claim + 찜(스펙 §2 G1 — ambiguous/amber는 찜 안 함).
  // candidate_order_ids가 빈 행만 즉시 찜(첫 찜 승리 — RPC 가드와 동일 조건 선filter);
  // stale 찜 행은 replace(X→Y 배열 CAS 단일 트랜잭션)로 교체(R4). 확신 대체 후보가 없으면
  // 아무 쓰기도 하지 않는다 — stale 찜을 빈 배열로 되돌리면 같은 버전의 지연 pin이 옛 후보를
  // 부활시킬 수 있어(ABA), DB엔 남기고 표시에서만 무시한다.
  if ((uiStatus === 'matched' || uiStatus === 'amount_suggested') && candidates.length === 1) {
    claimed.add(candidates[0].id);
    if (match.candidate_order_ids.length === 0) {
      pins.push({ depositMatchId: match.id, orderId: candidates[0].id, expectedModifiedAt: match.modified_at });
    } else if (stalePinOrderId !== null && stalePinOrderId !== candidates[0].id) {
      replaces.push({
        depositMatchId: match.id, expectedOrderId: stalePinOrderId,
        newOrderId: candidates[0].id, expectedModifiedAt: match.modified_at,
      });
    }
  }

  return { deposit, match, uiStatus, candidates, confirmedOrder: null };
}

// ── 배송비 구제 pass(스펙 §3, R2-#1) ─────────────────────────────────────────
// pass1(derivePendingItem)이 uiStatus·blue 클레임을 전부 계산한 뒤 실행되는 별도 순수 pass.
// amount_mismatch(amber) 항목에만 보조 필드 shippingRescue를 부착 — 기존 uiStatus·candidates·
// claim은 바이트 무변경(불변식 R2). "상품합계 + 등록된 다른 배송비(preset) = 입금액"의 정확·유일
// 일치를 찾아, 클릭 한 번 구제(배송비 교정+확정)를 제안한다.
//
// 반환 = rescue가 선점한 orderId 집합. pass3(참고후보)가 이 주문을 후보 리스트에서 제외해
// '한 행은 구제·다른 행은 같은 주문을 참고 후보로' 이중 제안을 차단한다(스펙 §3-7).
// 프리셋 [] 셀러는 즉시 no-op(불변식 R1) — 후보 순회조차 없다.
export function assignShippingRescues(
  items: DepositListItem[],
  config: ShippingConfig,
): Set<number> {
  const rescueClaimed = new Set<number>();
  const presets = [...new Set(config.shippingFeePresets)];   // 조건5: committed 프리셋 중복 제거
  if (presets.length === 0) return rescueClaimed;            // 불변식 R1: 미등록 셀러 no-op
  const presetSet = new Set(presets);

  // blue 선점 주문(조건7): 확신 추천(matched/amount_suggested, 후보 1건)이 잡은 주문. pass1의
  // claimed set과 동치 — claimed.add는 이 두 조건에서만 발생(derivePendingItem line 123·193).
  // items에서 재구성해 함수를 self-contained·순수하게 유지(판정표 수기 검증 용이).
  const blueClaimed = new Set<number>();
  for (const item of items) {
    if ((item.uiStatus === 'matched' || item.uiStatus === 'amount_suggested') && item.candidates.length === 1) {
      blueClaimed.add(item.candidates[0].id);
    }
  }

  // amber 행만, deposited_at 오름차순·동률 match.id 오름차순(조건7 — 표시 순서(최신순) 아님;
  // 리페치 간 소유권이 안 흔들리는 안정 키). first-wins로 rescue끼리 같은 주문을 다투지 않게 한다.
  const amberTs = (item: DepositListItem) =>
    new Date(item.deposit.deposited_at ?? item.deposit.created_at).getTime();
  const amber = items
    .filter((item) => item.uiStatus === 'amount_mismatch')
    .sort((a, b) => amberTs(a) - amberTs(b) || a.match.id - b.match.id);

  for (const item of amber) {
    const amount = item.deposit.amount;
    // amber 항목의 candidates는 byName 집합(조건1 — derivePendingItem amount_mismatch 분기 유일 소스).
    const combos: ShippingRescue[] = [];
    for (const o of item.candidates) {
      if (o.status === '부분 입금') continue;                                    // 조건2: 부분 입금 배제
      if (o.shippingFeeOverride !== null) continue;                             // 조건3: override IS NULL
      // (구 조건4 '무료배송 기준 미달'은 PR3 의미론 분리로 제거 — 프리셋 override는 기준 도달에도
      //  effectiveShipping이 그 금액을 반환하므로 total+p 등식이 구성상 성립. PR3 스펙 §3 참조.)
      if (blueClaimed.has(o.id) || rescueClaimed.has(o.id)) continue;           // 조건7: blue·선행 rescue 선점 제외
      const p = amount - o.total_price;                                         // 조건5: amount === total + p
      if (presetSet.has(p)) {
        combos.push({ orderId: o.id, presetAmount: p, expectedTotal: o.total_price + p });
      }
    }
    if (combos.length === 1) {                                                  // 조건6: (주문,프리셋) 정확히 1개
      item.shippingRescue = combos[0];
      rescueClaimed.add(combos[0].orderId);
    }
  }
  return rescueClaimed;
}

// ── 참고 후보(스펙 §4) ───────────────────────────────────────────────────────
// 완납형 = 이 입금 확정으로 주문이 '입금 완료'가 되는 일치. 미지정 ship=0·부분 입금 잔액
// 일치를 자연 포함하는 단일식(스펙 §4.1): 확정 후 합계가 임계값에 도달하는가.
export function isFullPaymentMatch(order: OrderCandidate, amount: number): boolean {
  return order.confirmedSum + amount >= order.paidThreshold;
}

// 시간 방향 랭크(스펙 §4.2): 입금 당일(0) > 과거 근접(1..N) > 미래 +1일(뒤로).
function timeRank(depositDateKst: string, orderDate: string): number {
  const diffDays = Math.round(
    (Date.parse(depositDateKst) - Date.parse(orderDate)) / 86400000);
  if (diffDays === 0) return 0;
  if (diffDays > 0) return diffDays;        // 과거: 가까울수록 작음
  return 100000 - diffDays;                 // 미래(+1일): 항상 과거 뒤
}

// 완전 순서 comparator(스펙 §4): ①완납형>상품비만 ②시간 방향·근접 ③created_at 최신
// ④id 내림차순(최종 결정 키). amount는 클로저로 고정 — 호출부가 입금별로 생성.
export function compareReferenceCandidates(depositDateKst: string, amount: number) {
  return (a: OrderCandidate, b: OrderCandidate): number => {
    const fullA = isFullPaymentMatch(a, amount) ? 0 : 1;
    const fullB = isFullPaymentMatch(b, amount) ? 0 : 1;
    if (fullA !== fullB) return fullA - fullB;
    const tA = timeRank(depositDateKst, a.order_date);
    const tB = timeRank(depositDateKst, b.order_date);
    if (tA !== tB) return tA - tB;
    const cA = Date.parse(a.created_at); const cB = Date.parse(b.created_at);
    if (cA !== cB) return cB - cA;
    return b.id - a.id;
  };
}

export interface DepositQueue {
  actionNeeded: DepositListItem[];
  excluded: DepositListItem[];   // 셀러가 '주문 아님'으로 제외한 입금(seller_rejected)
  confirmed: DepositListItem[];
}

export async function loadDepositQueue(userId: string): Promise<DepositQueue> {
  const [rows, config] = await Promise.all([
    repo.findDepositsWithMatch(),
    repo.getShippingConfig(userId),
  ]);
  // 이름맵은 이번 큐의 입금자명으로 스코프 로드(전체 parsed_order 스캔/1000행 truncation 회피).
  // rows를 받은 뒤라 한 번의 추가 왕복이 있지만, 스코프 쿼리가 작아 큰 셀러에선 오히려 빠르다.
  // ★confirmed·excluded(seller_rejected) 행은 빌드가 early-return이라 amber 불필요 → 이름맵
  //   스코프에서 제외(.in 목록 축소). 제외 아카이브가 쌓여도 .in(...) 목록이 무한정 커져 name map이
  //   전체 빈맵으로 degrade되는 것을 막는다(codexloop R1 F6).
  const depositorNames = [...new Set(
    rows
      .filter((r) => !r.match.confirmed_at && r.match.match_reason !== 'seller_rejected')
      .map((r) => r.deposit.depositor_name)
      .filter((n): n is string => !!n),
  )];
  const nameMap = await repo.getDepositorNicknameMap(userId, depositorNames);

  const settled = rows.filter((r) => r.match.confirmed_at || r.match.match_reason === 'seller_rejected');
  const pending = rows.filter((r) => !r.match.confirmed_at && r.match.match_reason !== 'seller_rejected');

  // settled(확정/제외)는 claimed와 무관 — 주문 1회 조회로 일괄 빌드(pending 파이프라인과 병렬 유지).
  const settledItemsP = buildSettledItems(settled, config, userId);

  // pending은 fetch(병렬) → derive(순차·결정적). 윈도우 쿼리는 (from,to) 키로 promise 캐시 —
  // 같은 날짜대 입금 N건이 쿼리 1회 공유(codexloop R1 F6).
  const windowCache = new Map<string, Promise<OrderCandidate[]>>();
  const fetchWindow = (from: string, to: string): Promise<OrderCandidate[]> => {
    const key = `${from}|${to}`;
    let p = windowCache.get(key);
    if (!p) { p = repo.findUnassignedOrdersInWindow(from, to, config, userId); windowCache.set(key, p); }
    return p;
  };
  const fetched = await Promise.all(pending.map((r) => fetchPendingData(r.deposit, r.match, config, userId, fetchWindow)));

  // 2-pass 배정(스펙 §3.3): 저장 후보(찜/엣지) 행이 먼저 claim — 동적 행이 시간상 앞서도 찜을
  // 침범하지 못한다. 각 pass 안은 입금시각 오름차순(오래된 입금 우선 = 결정적 배정).
  // 동시각(SMS 초 단위 충돌)은 match.id 오름차순 타이브레이크 — DB 응답 순서 의존 제거(R1 F5).
  const depositTs = (f: PendingFetch) => new Date(f.deposit.deposited_at ?? f.deposit.created_at).getTime();
  fetched.sort((a, b) => depositTs(a) - depositTs(b) || a.match.id - b.match.id);
  const claimed = new Set<number>();
  const pins: PinRequest[] = [];
  const replaces: ReplaceRequest[] = [];
  // 모순 찜 행(R2 F3)은 pass-1(저장 후보 우선권)에서 제외 — 동적 행으로 취급해야 더 오래된
  // 진짜 동적 행보다 먼저 claim하는 역전이 안 생긴다.
  const hasStoredPriority = (f: PendingFetch) => f.storedLive.length > 0 && !isPinContradicted(f, nameMap);
  const orderedFetched = [
    ...fetched.filter(hasStoredPriority),
    ...fetched.filter((f) => !hasStoredPriority(f)),
  ];
  const pendingItems = orderedFetched.map((f) => derivePendingItem(f, nameMap, claimed, pins, replaces));

  // ── 구제 pass(스펙 §3, R2-#1): pass1 완료 후 amber 항목에만 shippingRescue 보조 필드 부착 ──
  // (uiStatus·claimed 바이트 무변경). 반환 = rescue 선점 주문 → 참고 pass 입력에서 제외(pass3).
  const rescueClaimed = assignShippingRescues(pendingItems, config);

  // ── 참고 pass(스펙 §4): 기존 derive·claim이 전부 끝난 뒤 final claimed로 별도 배정 ──
  // 참고 풀은 (from60,to) 키로 promise 캐시 — 같은 날짜대 입금이 fetch 1회 공유(F6 원리 재사용).
  const refCache = new Map<string, Promise<OrderCandidate[] | null>>();
  const fetchRefPool = (base: string): Promise<OrderCandidate[] | null> => {
    const from = toKstDateString(base, -60);
    const to = toKstDateString(base, 1);
    const key = `${from}|${to}`;
    let p = refCache.get(key);
    if (!p) { p = repo.findReferenceOrdersInWindow(from, to, config, userId); refCache.set(key, p); }
    return p;
  };

  // 대상 행(스펙 §3): unmatched 또는 amber, 모순 찜 제외. zip 인덱스로 PendingFetch 복원.
  type RefRow = { item: DepositListItem; f: PendingFetch; poolP: Promise<OrderCandidate[] | null> };
  const refRows: RefRow[] = [];
  pendingItems.forEach((item, i) => {
    const f = orderedFetched[i];
    if (item.uiStatus !== 'unmatched' && item.uiStatus !== 'amount_mismatch') return;
    if (isPinContradicted(f, nameMap)) return;   // 스펙 §3 예외 1
    refRows.push({ item, f, poolP: fetchRefPool(f.deposit.deposited_at ?? f.deposit.created_at) });
  });
  const pools = await Promise.all(refRows.map((r) => r.poolP));

  // 배정 순서(스펙 §4, R3 F2·R4 F1): ①unmatched 먼저 ②풀 작은 입금 먼저 ③기존 결정적 순차.
  // 풀 크기는 '그 행이 실제 못 받는 주문'을 뺀 유효 후보 수로 계산해야 공정하다.
  const referenceClaimed = new Set<number>();
  const withPools = refRows.map((r, i) => {
    const pool = pools[i];
    if (pool === null) return { ...r, seq: i, eligible: [] as OrderCandidate[] };  // truncation/실패 → 참고만 fail-closed
    const ownCandidateIds = new Set(r.item.candidates.map((c) => c.id));
    const gate = paidAtGateFor(r.f.deposit);
    const eligible = pool.filter((o) =>
      o.expectedAmounts.includes(r.f.deposit.amount) &&   // 정확일치(부분 입금=[잔액] 재정의 그대로)
      !claimed.has(o.id) &&                               // final claimed 제외
      !rescueClaimed.has(o.id) &&                         // pass3: 구제 선점 주문 제외(이중 제안 차단, 스펙 §3-7)
      !ownCandidateIds.has(o.id) &&                       // 자기 amber 후보와 중복 제거
      gate(o),                                            // 시간 게이트(스펙 §8) — 랭킹·referenceClaimed 선점 이전 적용
    );
    return { ...r, seq: i, eligible };
  });
  withPools.sort((a, b) => {
    const tierA = a.item.uiStatus === 'unmatched' ? 0 : 1;
    const tierB = b.item.uiStatus === 'unmatched' ? 0 : 1;
    if (tierA !== tierB) return tierA - tierB;
    if (a.eligible.length !== b.eligible.length) return a.eligible.length - b.eligible.length;
    return a.seq - b.seq;
  });
  for (const row of withPools) {
    const cmp = compareReferenceCandidates(
      toKstDateString(row.f.deposit.deposited_at ?? row.f.deposit.created_at), row.f.deposit.amount);
    const ranked = row.eligible
      .filter((o) => !referenceClaimed.has(o.id))   // 표시 dedupe(스펙 §6.1)
      .sort(cmp)
      .slice(0, 3);                                 // 표시 상한 — 표시분만 선점(R3 F2)
    if (ranked.length > 0) {
      row.item.referenceCandidates = ranked;
      ranked.forEach((o) => referenceClaimed.add(o.id));
    }
  }

  const items = [...(await settledItemsP), ...pendingItems];

  // 찜 저장/교체는 fire-and-forget — 큐 반환을 막지 않고, 실패(no-op)해도 다음 로드에서 재시도.
  // realtime 구독(Home)이 이 UPDATE로 refresh 1회를 유발하지만, 그 로드에선 저장 후보 경로라
  // 새 쓰기가 안 생겨 자가 종료(무한 루프 없음).
  for (const p of pins) void repo.pinDepositCandidates(p.depositMatchId, p.orderId, p.expectedModifiedAt);
  for (const r of replaces) {
    void repo.replaceDepositCandidates(r.depositMatchId, r.expectedOrderId, r.newOrderId, r.expectedModifiedAt);
  }

  const ts = (i: DepositListItem) => new Date(i.deposit.deposited_at ?? i.deposit.created_at).getTime();
  items.sort((a, b) => ts(b) - ts(a));
  return {
    actionNeeded: items.filter((i) => i.uiStatus !== 'confirmed' && i.uiStatus !== 'excluded'),
    excluded: items.filter((i) => i.uiStatus === 'excluded'),
    confirmed: items.filter((i) => i.uiStatus === 'confirmed'),
  };
}

// 파랑 '입금 들어옴' 카드 도출 — 순수(loadDepositIncoming이 사용).
// 금액 정확일치·미확정·확실(matched/amount_suggested) 매칭 전부. 필터/맵 로직은 기존과 바이트 동일.
function deriveIncomingCards(queue: DepositQueue): DepositIncomingCard[] {
  return queue.actionNeeded
    .filter((item) => {
      if (item.uiStatus !== 'matched' && item.uiStatus !== 'amount_suggested') return false;
      const c = item.candidates[0];
      if (!c) return false;
      return c.expectedAmounts.includes(item.deposit.amount);
    })
    .map((item) => {
      const c = item.candidates[0];
      return {
        matchId: item.match.id,
        orderId: c.id,
        nickname: c.nickname,
        depositorName: item.deposit.depositor_name,
        amount: item.deposit.amount,
        orderDate: c.order_date,
        depositedAt: item.deposit.deposited_at ?? item.deposit.created_at,
        version: item.match.modified_at,
        paidThreshold: c.paidThreshold,
        remainingAmount: c.remainingAmount,
        totalPrice: c.total_price,
        shippingFeeOverride: c.shippingFeeOverride,
        // 지문 운반 계약: 후보에 로드 시 부착된 지문(paidThreshold와 같은 config 스냅샷)을 카드로 운반.
        settingsFingerprint: c.settingsFingerprint,
      };
    });
}

// '입금 들어옴' 파랑 카드 로드 — useDepositIncoming이 사용. 참고 후보(회색 카드)의 /order
// 노출은 오너 결정(D8, 2026-07-06)으로 제거돼 파랑만 파생한다(참고는 /deposits 전용,
// loadDepositQueue의 referenceCandidates 필드로 소비).
export async function loadDepositIncoming(userId: string): Promise<DepositIncomingCard[]> {
  const queue = await loadDepositQueue(userId);
  return deriveIncomingCards(queue);
}

export const undoMatch = repo.undoMatch;
// ✗(무시/제외): 잘못 매칭된/주문 아닌 미확정 입금을 seller_rejected로 마킹 → '제외됨' 섹션으로.
export const rejectMatch = repo.rejectMatch;
// 제외 복구: seller_rejected를 풀어 다시 확인 대기로(재도출).
export const restoreMatch = repo.restoreMatch;
export const subscribeToDepositMatches = repo.subscribeToDepositMatches;
// nav 배지 카운트(증분2) — 정의는 repository, 소비는 useDepositActionCount.
export const countActionNeeded = repo.countActionNeeded;
// nav 배지 전용 구독(deposit_match + seller_deposit_account 한 채널) — useDepositActionCount가 소비.
export const subscribeToDepositBadge = repo.subscribeToDepositBadge;

// 단계3: 매칭 확정 + 다리 학습. 모든 수동확정 경로(Home 입금카드·/deposits)가 이걸 쓴다(학습 멱등).
export const linkMatch = repo.linkMatch;

// ── 배송비 구제 확정 체인 지원(스펙 §5, PR2) ─────────────────────────────────
// Step 2·undo 원복 CAS는 repository CAS로 위임(페이지 직접 supabase 금지 — service 경유).
export const applyRescueShippingOverride = repo.applyRescueShippingOverride;
export const revertRescueShippingOverride = repo.revertRescueShippingOverride;
export const isRescueOverrideStuck = repo.isRescueOverrideStuck;

// ── 부분입금 통합 관리(스펙 §16) — 정의는 repository, 소비는 usePartialDepositOverview ──
// A(자동 전환): 임계값·remaining은 overview가 DB 단일 소스로 반환(§16.3) — 클라 config 로드 없음.
// convert = 안전 레인 수동 fallback(§16.8), settle = up-manual 마감(§16.7). disable은 A에서 제거(§16.1-4).
export const fetchPartialDepositOverview = repo.fetchPartialDepositOverview;
export const convertPaidOrderToPartial = repo.convertPaidOrderToPartial;
export const settlePartialOrder = repo.settlePartialOrder;

// 구제 재검증(스펙 §5.1 카드 클릭 시 + §5.2 ConfirmGuard 제출 시, 총 2회): loadDepositQueue를
// fresh 재실행해 §3 조건 전체를 클릭/제출 시점 기준으로 재판정한다. 별도 판정 로직을 두지 않고
// 동일 파생(assignShippingRescues)을 재사용 = 로드 시점과 100% 동일 규칙(2-pass claim·유일성·
// blue 선점 제외까지 자동 포함). 여전히 같은 (orderId, presetAmount)로 rescue가 산출되면 fresh
// 후보·완납액·match 버전을 반환, 탈락(주문 변경·프리셋 삭제·유일성 상실·override 생김·이미 확정
// 등)이면 null → 호출측이 제안 철회 + refetch.
export async function revalidateShippingRescue(
  userId: string, matchId: number, orderId: number, presetAmount: number,
): Promise<RescueRevalidation | null> {
  const queue = await loadDepositQueue(userId);
  const item = queue.actionNeeded.find((it) => it.match.id === matchId);
  if (!item || item.uiStatus !== 'amount_mismatch' || !item.shippingRescue) return null;
  const r = item.shippingRescue;
  // 같은 주문·같은 프리셋으로 여전히 유효할 때만 통과(다른 주문/프리셋으로 옮겨갔으면 = 드리프트 → 철회).
  if (r.orderId !== orderId || r.presetAmount !== presetAmount) return null;
  const order = item.candidates.find((c) => c.id === orderId);
  if (!order) return null;
  return { order, presetAmount: r.presetAmount, expectedTotal: r.expectedTotal, expectedModifiedAt: item.match.modified_at };
}

// 수동연결 픽커: 기존 -14~+1 base는 그대로 두고(무회귀), -60~-15 delta를 별도 fetch로 merge.
// delta 실패/truncation/합산 degrade는 base에 영향 없음 — Sentry 로깅 후 base만 반환(R3 F6).
export async function loadManualLinkCandidates(deposit: ParsedDeposit, userId: string): Promise<OrderCandidate[]> {
  const config = await repo.getShippingConfig(userId);
  const base = deposit.deposited_at ?? deposit.created_at;
  const from14 = toKstDateString(base, -14);
  const to = toKstDateString(base, 1);
  const from60 = toKstDateString(base, -60);
  const to15 = toKstDateString(base, -15);
  const [baseRows, deltaRows] = await Promise.all([
    repo.findRecentUnassignedOrders(from14, to, config, userId),          // 기존 경로 그대로(throw 유지)
    repo.findReferenceOrdersInWindow(from60, to15, config, userId),      // no-throw, null=격리 실패
  ]);
  if (deltaRows === null) return baseRows;
  const seen = new Set(baseRows.map((r) => r.id));
  return [...baseRows, ...deltaRows.filter((r) => !seen.has(r.id))];     // base 앞 보존 + id dedupe
}
